import { useEffect, useRef } from 'react';

const BG = '#212224';
const FG = '#3e3f42';
const BANDS = 9;
const SCALE = 0.0038;
const SPEED = 0.00004;
const CELL = 4;
const FRAME_MS = 1000 / 30;

// ===== Noise =====

function smoothstep(t: number) {
    t = Math.max(0, Math.min(1, t));
    return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number) { return a + (b - a) * t}

function hash(ix: number, iy: number, seed: number): number {
  let h = Math.imul(ix ^ seed, 374761393) ^ Math.imul(iy, 668265263);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 0x100000000;
}

function vnoise(x: number, y: number, seed: number): number {
  const ix = Math.floor(x), iy = Math.floor(y);
  const ux = smoothstep(x - ix), uy = smoothstep(y - iy);
  return lerp(
    lerp(hash(ix, iy, seed), hash(ix + 1, iy, seed), ux),
    lerp(hash(ix, iy + 1, seed), hash(ix + 1, iy + 1, seed), ux),
    uy,
  );
}

function fbm(x: number, y: number, seed: number): number {
  let v = 0, amp = 0.5, freq = 1, sum = 0;
  for (let o = 0; o < 4; o++) {
    v += vnoise(x * freq, y * freq, seed ^ (o * 73856093)) * amp;
    sum += amp; amp *= 0.5; freq *= 2.1;
  }
  return v / sum;
}

// ===== Grid =====

function buildGrid(cols: number, rows: number, xOff: number, seed: number): Float32Array {
  const w = cols + 1;
  const g = new Float32Array(w * (rows + 1));
  let mn = Infinity, mx = -Infinity;
  for (let j = 0; j <= rows; j++) {
    for (let i = 0; i <= cols; i++) {
      const v = fbm(i * SCALE + xOff, j * SCALE, seed);
      g[j * w + i] = v;
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    }
  }
  const r = mx - mn || 1;
  for (let k = 0; k < g.length; k++) g[k] = (g[k] - mn) / r;
  return g;
}

// ===== Marching sqrs =====

function ip(v1: number, v2: number, t: number) {
  return Math.abs(v2 - v1) < 1e-4 ? 0.5 : (t - v1) / (v2 - v1);
}

function drawContours(
  ctx: CanvasRenderingContext2D,
  g: Float32Array,
  cols: number,
  rows: number,
) {
  const w = cols + 1;
  const major = Math.floor(BANDS / 4);

  // Two passes: thin minor lines at lower opacity, slightly thicker major lines
  for (const pass of [0, 1] as const) {
    ctx.strokeStyle = FG;
    ctx.globalAlpha = pass === 0 ? 0.5 : 1;
    ctx.lineWidth = pass === 0 ? 0.55 : 0.9;
    ctx.beginPath();

    for (let band = 1; band < BANDS; band++) {
      const isMaj = band % major === 0;
      if (pass === 0 && isMaj) continue;
      if (pass === 1 && !isMaj) continue;

      const thresh = band / BANDS;
      for (let j = 0; j < rows; j++) {
        for (let i = 0; i < cols; i++) {
          const a = g[j * w + i],     b = g[j * w + i + 1];
          const c = g[(j + 1) * w + i], d = g[(j + 1) * w + i + 1];
          const x = i * CELL, y = j * CELL;
          const pts: [number, number][] = [];
          if ((a < thresh) !== (b < thresh)) pts.push([x + ip(a, b, thresh) * CELL, y]);
          if ((b < thresh) !== (d < thresh)) pts.push([x + CELL, y + ip(b, d, thresh) * CELL]);
          if ((c < thresh) !== (d < thresh)) pts.push([x + ip(c, d, thresh) * CELL, y + CELL]);
          if ((a < thresh) !== (c < thresh)) pts.push([x, y + ip(a, c, thresh) * CELL]);
          if (pts.length === 2) {
            ctx.moveTo(pts[0][0], pts[0][1]);
            ctx.lineTo(pts[1][0], pts[1][1]);
          }
        }
      }
    }
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

// ===== Component =====

export function TopoBackground({ seed }: { seed: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const state = useRef({ xOff: 0, cols: 0, rows: 0, lastTs: 0 });
  const raf = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    const s = state.current;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function resize() {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      s.cols = Math.ceil(canvas.width / CELL);
      s.rows = Math.ceil(canvas.height / CELL);
    }

    function render() {
      ctx.fillStyle = BG;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      drawContours(ctx, buildGrid(s.cols, s.rows, s.xOff, seed), s.cols, s.rows);
    }

    resize();

    if (reduced) {
      render();
      return;
    }

    function loop(ts: number) {
      const dt = ts - s.lastTs;
      if (dt >= FRAME_MS) {
        s.xOff += SPEED * Math.min(dt, FRAME_MS * 3);
        s.lastTs = ts - (dt % FRAME_MS);
        render();
      }
      raf.current = requestAnimationFrame(loop);
    }

    raf.current = requestAnimationFrame((ts) => {
      s.lastTs = ts;
      render();
      raf.current = requestAnimationFrame(loop);
    });

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(raf.current);
      ro.disconnect();
    };
  }, [seed]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}
    />
  );
}